import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import {
  AlertTriangle, Clock, TrendingUp, Package, FileText, Users, Zap,
  Mail, ChevronRight, RefreshCw, Target, ArrowUp, ArrowDown, Truck
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function StatCard({ label, value, icon: Icon, color, sub }) {
  return (
    <div style={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 10, padding: "14px 18px", borderLeft: `4px solid ${color}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 28, fontWeight: 800, color, fontVariantNumeric: "tabular-nums" }}>{value}</div>
          <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 500, marginTop: 2 }}>{label}</div>
          {sub && <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{sub}</div>}
        </div>
        <Icon size={22} style={{ color: color + "60" }} />
      </div>
    </div>
  );
}

function TargetBar({ label, actual, target, pct }) {
  const isOver = pct >= 100;
  const isLow = pct < 50;
  const barColor = isOver ? "#22c55e" : isLow ? "#ef4444" : "#f59e0b";
  const cappedPct = Math.min(pct, 100);
  return (
    <div style={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 10, padding: "14px 18px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#e2e8f0" }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: barColor }}>
          {actual} / {target} <span style={{ fontSize: 11, color: "#64748b" }}>({pct}%)</span>
        </span>
      </div>
      <div style={{ background: "#1e293b", borderRadius: 6, height: 10, overflow: "hidden" }}>
        <div style={{ width: `${cappedPct}%`, height: "100%", background: barColor, borderRadius: 6, transition: "width 0.8s ease" }} />
      </div>
    </div>
  );
}

function ActionQueue({ title, items, columns, color, icon: Icon }) {
  if (!items || items.length === 0) {
    return (
      <div style={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 10, padding: "14px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <Icon size={16} style={{ color }} />
          <span style={{ fontSize: 13, fontWeight: 700, color }}>{title}</span>
          <span style={{ fontSize: 11, color: "#22c55e", fontWeight: 600, marginLeft: "auto" }}>All clear</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 10, padding: "14px 18px", maxHeight: 320, display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <Icon size={16} style={{ color }} />
        <span style={{ fontSize: 13, fontWeight: 700, color }}>{title}</span>
        <span style={{
          fontSize: 11, fontWeight: 700, color: "white",
          background: items.length > 10 ? "#ef4444" : items.length > 5 ? "#f59e0b" : "#64748b",
          padding: "2px 8px", borderRadius: 10, marginLeft: "auto"
        }}>{items.length}</span>
      </div>
      <div style={{ fontSize: 10, display: "grid", gridTemplateColumns: columns.map(c => c.w || "1fr").join(" "), gap: 4, padding: "4px 0", borderBottom: "1px solid #1e293b", color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {columns.map((c, i) => <div key={i}>{c.label}</div>)}
      </div>
      <div style={{ flex: 1, overflowY: "auto" }}>
        {items.slice(0, 15).map((item, i) => (
          <div key={i} style={{
            display: "grid", gridTemplateColumns: columns.map(c => c.w || "1fr").join(" "), gap: 4,
            padding: "5px 0", borderBottom: "1px solid #0f172a", fontSize: 12, color: "#cbd5e1",
            background: item.critical ? "#7f1d1d20" : "transparent",
          }}>
            {columns.map((c, j) => (
              <div key={j} style={{
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                color: c.key === "action" ? "#38bdf8" : c.key === "hours_stuck" || c.key === "days_pending" || c.key === "days_inactive" || c.key === "hours_since_creation"
                  ? (item[c.key] > (c.critical || 24) ? "#ef4444" : "#f59e0b") : "#cbd5e1",
                fontWeight: c.key === "action" ? 500 : 400,
              }}>
                {c.key === "hours_stuck" || c.key === "hours_since_creation" ? `${item[c.key]}h`
                  : c.key === "days_pending" || c.key === "days_inactive" ? `${item[c.key]}d`
                  : item[c.key] || "-"}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function OperationsMonitorPage() {
  const { token } = useAuth();
  const [scorecard, setScorecard] = useState(null);
  const [bars, setBars] = useState([]);
  const [queues, setQueues] = useState({});
  const [lastRefresh, setLastRefresh] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    try {
      const [scRes, barRes, qRes] = await Promise.all([
        fetch(`${API}/api/operations-monitor/scorecard`, { headers }),
        fetch(`${API}/api/operations-monitor/target-vs-actual`, { headers }),
        fetch(`${API}/api/operations-monitor/action-queues`, { headers }),
      ]);
      if (scRes.ok) setScorecard(await scRes.json());
      if (barRes.ok) setBars((await barRes.json()).bars || []);
      if (qRes.ok) setQueues((await qRes.json()).queues || {});
      setLastRefresh(new Date());
    } catch (e) {
      console.error("Fetch error:", e);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 60000); // Auto-refresh every 60s
    return () => clearInterval(interval);
  }, [fetchAll]);

  const sc = scorecard || {};
  const now = new Date();
  const timeStr = now.toLocaleTimeString("sl-SI", { hour: "2-digit", minute: "2-digit" });
  const dateStr = now.toLocaleDateString("sl-SI", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <>
      <Navbar />
      <main style={{ background: "#020617", minHeight: "100vh", padding: "72px 20px 20px", color: "#e2e8f0" }} data-testid="ops-monitor">
        <div style={{ maxWidth: 1400, margin: "0 auto" }}>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 800, color: "#f1f5f9", margin: 0, letterSpacing: "-0.5px" }}>
                <Target size={20} style={{ marginRight: 8, verticalAlign: "middle", color: "#38bdf8" }} />
                Operations Control
              </h1>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{dateStr} | {timeStr} CET</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {lastRefresh && <span style={{ fontSize: 11, color: "#475569" }}>Updated {lastRefresh.toLocaleTimeString("sl-SI", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>}
              <button onClick={fetchAll} style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 6, padding: "6px 12px", color: "#94a3b8", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
                <RefreshCw size={13} /> Refresh
              </button>
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: 60, color: "#475569" }}>Loading operational data...</div>
          ) : (
            <>
              {/* TOP BAND — Today's Flow */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 10, marginBottom: 16 }}>
                <StatCard label="PFs Opened" value={sc.pfs_opened_today ?? 0} icon={Package} color="#3b82f6" />
                <StatCard label="PFs Closed" value={sc.pfs_closed_today ?? 0} icon={FileText} color="#22c55e" />
                <StatCard label="Declarations" value={sc.declarations_today ?? 0} icon={Zap} color="#a855f7" />
                <StatCard label="Revenue (EUR)" value={`${(sc.revenue_today_eur ?? 0).toLocaleString("de-DE", { maximumFractionDigits: 0 })}`} icon={TrendingUp} color="#10b981" sub="Today" />
                <StatCard label="Avg Clearance" value={`${sc.avg_clearance_hours ?? 0}h`} icon={Clock} color="#0ea5e9" sub="Last 7d" />
                <StatCard label="Stuck Files" value={sc.stuck_files_count ?? 0} icon={AlertTriangle} color={(sc.stuck_files_count ?? 0) > 10 ? "#ef4444" : "#f59e0b"} />
                <StatCard label="TOs Pending" value={sc.transport_orders_pending ?? 0} icon={Truck} color="#f97316" />
              </div>

              {/* MIDDLE BAND — Target vs Actual */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 16 }}>
                {bars.map((b, i) => (
                  <TargetBar key={i} label={b.label} actual={b.actual} target={b.target} pct={b.pct} />
                ))}
                {bars.length === 0 && (
                  <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: 20, color: "#475569", fontSize: 13 }}>
                    No targets configured. <button onClick={async () => {
                      await fetch(`${API}/api/operations-monitor/targets/seed`, { method: "POST", headers });
                      fetchAll();
                    }} style={{ color: "#38bdf8", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Seed defaults</button>
                  </div>
                )}
              </div>

              {/* BOTTOM BAND — Action Queues */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <ActionQueue
                  title="Files Stuck >6h"
                  icon={AlertTriangle}
                  color="#ef4444"
                  items={queues.stuck_files?.items || []}
                  columns={[
                    { label: "PF", key: "pf_number", w: "110px" },
                    { label: "Customer", key: "customer", w: "1fr" },
                    { label: "Staff", key: "staff", w: "100px" },
                    { label: "Hours", key: "hours_stuck", w: "55px", critical: 24 },
                    { label: "Action", key: "action", w: "1.2fr" },
                  ]}
                />
                <ActionQueue
                  title="Offers Awaiting Reply >3d"
                  icon={Mail}
                  color="#f59e0b"
                  items={queues.open_offers?.items || []}
                  columns={[
                    { label: "Offer", key: "reference", w: "100px" },
                    { label: "Customer", key: "customer", w: "1fr" },
                    { label: "Staff", key: "staff", w: "90px" },
                    { label: "Days", key: "days_pending", w: "50px", critical: 7 },
                    { label: "Action", key: "action", w: "1.2fr" },
                  ]}
                />
                <ActionQueue
                  title="Inactive Customers >14d"
                  icon={Users}
                  color="#8b5cf6"
                  items={queues.inactive_customers?.items || []}
                  columns={[
                    { label: "Customer", key: "customer", w: "1fr" },
                    { label: "Email", key: "email", w: "1fr" },
                    { label: "Days", key: "days_inactive", w: "50px", critical: 30 },
                    { label: "Action", key: "action", w: "1.2fr" },
                  ]}
                />
                <ActionQueue
                  title="PFs Without Milestones"
                  icon={Package}
                  color="#0ea5e9"
                  items={queues.no_milestone?.items || []}
                  columns={[
                    { label: "PF", key: "pf_number", w: "110px" },
                    { label: "Customer", key: "customer", w: "1fr" },
                    { label: "Staff", key: "staff", w: "90px" },
                    { label: "Hours", key: "hours_since_creation", w: "55px", critical: 48 },
                    { label: "Action", key: "action", w: "1.2fr" },
                  ]}
                />
              </div>
            </>
          )}
        </div>
      </main>
    </>
  );
}
